import React from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router";
import { useForm, FormProvider, type Resolver } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { ClientConfiguration } from "../../models/ClientConfiguration/ClientConfiguration";
import { createClientConfigurationSchema } from "@/lib/schemas/clientConfigurationSchema";
import ClientConfigurationsRepository from "../../repositories/ClientConfigurationsRepository";
import { ImplementationType } from "../../models/OrganizationConfiguration/ImplementationType";
import AzureBlobSection from "./AzureBlobSection";
import ClientSettingsSection from "./ClientSettingsSection";
import MigrationConfigurationSection from "./MigrationConfigurationSection";
import SharePointSection from "./SharePointSection";
import MappingsSettingsSection from "./MappingsSettingsSection";
import ApiSection from "./ApiSection";
import FieldError from "../common/FieldError";

import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Separator } from "@/components/ui/separator";
import { cn } from "@/lib/utils";
import { SlidersHorizontal } from "lucide-react";
import { toast } from "sonner";

import { UserRole } from "../../models/User";
import ActionConfirmationModal, { ACTION_CONFIGS } from "../common/ActionConfirmationModal";
import { QUERY_KEYS } from "../../constants/QueryKeys";
import { buildValidationMessage, reportAndScrollToFormErrors } from "@/lib/form-utils";
import DocsHelpLink from "@/components/Docs/DocsHelpLink";
import { useRequiresReason } from "../../hooks/useRequiresReason";

interface ClientConfigurationFormProps {
  organizationId: string;
  configuration: ClientConfiguration;
  implementationType: ImplementationType;
  userRole: UserRole;
  isAssignedUser?: boolean;
}

const ClientConfigurationForm: React.FC<ClientConfigurationFormProps> = ({
  organizationId,
  configuration,
  implementationType,
  userRole,
  isAssignedUser
}) => {
  const requiresReason = useRequiresReason();
  const [isUploading, setUploading] = React.useState(false);
  const configurationsRepository = new ClientConfigurationsRepository();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const form = useForm<ClientConfiguration>({
    defaultValues: configuration,
    // The zod schema's nested shapes (AzureBlob, DocTypeMapping, etc.) model
    // `string | undefined` where the TS model uses `string | null | undefined`,
    // so the resolver is narrowed to the form's working type.
    resolver: zodResolver(createClientConfigurationSchema(implementationType)) as Resolver<ClientConfiguration>,
    // RHF's default shouldFocusError calls .focus() on the first invalid input,
    // which triggers the browser to scroll *every* scrollable ancestor. That
    // pushes the DashboardPage breadcrumb (and eventually the sticky action
    // bar) out of view via programmatic scroll on <main>'s overflow-hidden box.
    // reportAndScrollToErrors handles the scroll instead, so auto-focus stays off.
    shouldFocusError: false,
  });

  const formatConfigError = (error: any): string => {
    const data = error?.response?.data;
    if (data) {
      const parts: string[] = [];
      if (data.title) parts.push(String(data.title));
      if (data.errors && typeof data.errors === "object") {
        const entries = Object.entries(data.errors).map(([key, val]) => {
          const messages = Array.isArray(val) ? val.join("; ") : String(val);
          return `${key}: ${messages}`;
        });
        if (entries.length) parts.push(entries.join(" | "));
      } else if (data.message) {
        parts.push(String(data.message));
      }
      if (parts.length) return parts.join(" - ");
    }
    return error?.message ?? "Failed to save configuration";
  };

  const configMutation = useMutation({ mutationKey: ["clientConfigurations", organizationId], mutationFn:
    async (params: ClientConfiguration & { reason?: string }) => {
      const { reason, ...updatedConfig } = params;

      try {
        if (updatedConfig.id) {
          return await configurationsRepository.updateClientConfiguration(
            updatedConfig.id || "",
            updatedConfig,
            reason
          );
        } else {
          if (!updatedConfig.dbConnectionString) {
            updatedConfig.dbConnectionString = " ";
          }
          if (!updatedConfig.sharepointTenantId) {
            updatedConfig.sharepointTenantId = " ";
          }

          const newConfig =
            await configurationsRepository.createClientConfiguration(
              updatedConfig,
              reason
            );
          navigate(
            `/organization/${organizationId}/client-configuration/${newConfig.id}`,
          );
          return newConfig;
        }
      } catch (error: any) {
        // Rethrow with a flattened message so awaiting callers (and
        // ActionConfirmationModal) get the rich validation details.
        throw new Error(formatConfigError(error));
      }
    }, onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.CLIENT_CONFIGURATIONS] });
        queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.CLIENT_INFOS] });

        // Versioning rows are resolved server-side from this configuration's own root drive names and
        // site name, so saving either can change which library a row describes — or whether it resolves
        // at all. Without these the panel keeps the pre-save resolution, and its "unsaved" hint compares
        // the form against the stale server value and so survives the save that settled it.
        queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.GRAPH_LIBRARY_VERSIONING] });
        queryClient.invalidateQueries({
          queryKey: [QUERY_KEYS.ORGANIZATION_GRAPH_LIBRARY_VERSIONING],
        });
      },
      onSettled: () => {
        setUploading(false);
      }, });

  const validateAndSubmit = (reason?: string) => {
    form.handleSubmit(
      async (data: ClientConfiguration) => {
        setUploading(true);
        try {
          await configMutation.mutateAsync({ ...data, reason });
          toast.success(
            data.id
              ? "Client configuration updated"
              : "Client configuration created",
          );
        } catch (error: any) {
          toast.error(error?.message ?? "Failed to save configuration");
        }
      },
      () => {
        void reportAndScrollToFormErrors(form);
      }
    )();
  };

  const configId = form.watch("id");

  return (
    <FormProvider {...form}>
      <form onSubmit={(e) => e.preventDefault()}>
        <div
          data-sticky-action-bar
          className={cn(
            "sticky top-0 z-30 bg-background border-b border-border/60",
            isUploading && "opacity-50 pointer-events-none"
          )}
        >
          <div className="px-6 md:px-12 py-2 flex gap-3 flex-col sm:flex-row items-start sm:items-center flex-wrap justify-between">
            <div className="flex-1 min-w-0 flex items-center gap-2.5 group">
              <SlidersHorizontal className="size-5 text-muted-foreground shrink-0" />
              <div className="flex-1 min-w-0">
                <Input
                  onChange={(event) => form.setValue("name", event.target.value)}
                  className={cn(
                    "text-base font-semibold h-8 px-2 shadow-none transition-colors",
                    "border border-transparent hover:border-input/60",
                    "focus-visible:border-input focus-visible:ring-1",
                  )}
                  value={form.watch("name") ?? ""}
                  placeholder="Untitled configuration"
                  disabled={userRole === UserRole.Viewer}
                />
                <FieldError name="name" />
              </div>
            </div>
            <div className="flex gap-2 shrink-0 items-center">
              <DocsHelpLink to="configuration/client-configuration" />
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={() => navigate(`/organization/${organizationId}/client-configurations`)}
              >
                Back
              </Button>

              {requiresReason ? (
                <ActionConfirmationModal
                  actionConfig={configId ? ACTION_CONFIGS.UPDATE_CLIENT_CONFIGURATION : ACTION_CONFIGS.CREATE_CLIENT_CONFIGURATION}
                  onConfirm={async (reason) => {
                    const isValid = await form.trigger();
                    if (!isValid) {
                      const count = await reportAndScrollToFormErrors(form);
                      // Throw an empty-message error so ActionConfirmationModal
                      // keeps the modal open without duplicating the toast
                      // reportAndScrollToFormErrors has already fired.
                      const err = new Error(buildValidationMessage(count));
                      (err as Error & { __suppressToast?: boolean }).__suppressToast = true;
                      throw err;
                    }
                    const data = form.getValues();
                    setUploading(true);
                    // Await so ActionConfirmationModal's success/error toasts
                    // reflect the actual mutation outcome.
                    await configMutation.mutateAsync({ ...data, reason });
                  }}
                >
                  <Button
                    type="button"
                    size="sm"
                    disabled={configMutation.isPending}
                  >
                    {configId ? "Submit" : "Create"}
                  </Button>
                </ActionConfirmationModal>
              ) : (
                <Button
                  type="button"
                  size="sm"
                  disabled={configMutation.isPending || userRole === UserRole.Viewer}
                  onClick={() => validateAndSubmit()}
                >
                  {configId ? "Submit" : "Create"}
                </Button>
              )}
            </div>
          </div>
        </div>

        <div className="px-6 md:px-12 pt-4 pb-2 grid grid-cols-12 gap-4">

          <div className="col-span-12">
            <div className={cn(isUploading && "opacity-50 pointer-events-none")}>
              <Card>
                <CardContent>
                  <h4 className="text-lg font-semibold">
                    SharePoint
                  </h4>
                  <Separator className="mt-2 mb-2" />
                  <SharePointSection
                    organizationId={organizationId}
                    clientConfigurationId={configuration?.id}
                    disabled={userRole === UserRole.Viewer}
                  />
                </CardContent>
              </Card>
            </div>
          </div>

          <div className="col-span-12">
            <div className={cn(isUploading && "opacity-50 pointer-events-none")}>
              <Card>
                <CardContent>
                  <h4 className="text-lg font-semibold">
                    Client
                  </h4>
                  <Separator className="mt-2 mb-2" />
                  <ClientSettingsSection
                    implementationType={implementationType}
                    disabled={userRole === UserRole.Viewer}
                  />
                </CardContent>
              </Card>
            </div>
          </div>

          {implementationType === ImplementationType.AzureBlob ? (
            <div className="col-span-12">
              <div className={cn(isUploading && "opacity-50 pointer-events-none")}>
                <Card>
                  <CardContent>
                    <h4 className="text-lg font-semibold">
                      Azure Blob
                    </h4>
                    <Separator className="mt-2 mb-2" />
                    <AzureBlobSection
                      disabled={userRole === UserRole.Viewer}
                    />
                  </CardContent>
                </Card>
              </div>
            </div>
          ) : (
            <></>
          )}

          {implementationType === ImplementationType.Filevine
            || implementationType === ImplementationType.Dropbox ? (
            <div className="col-span-12">
              <div className={cn(isUploading && "opacity-50 pointer-events-none")}>
                <Card>
                  <CardContent>
                    <h4 className="text-lg font-semibold">
                      {implementationType === ImplementationType.Filevine
                        ? "Filevine Connection"
                        : implementationType === ImplementationType.Dropbox
                          ? "Dropbox Connection"
                          : "API Connection"}
                    </h4>
                    <Separator className="mt-2 mb-2" />
                    <ApiSection
                      organizationId={organizationId}
                      implementationType={implementationType}
                      disabled={userRole === UserRole.Viewer}
                    />
                  </CardContent>
                </Card>
              </div>
            </div>
          ) : (
            <></>
          )}

          {implementationType !== ImplementationType.Filevine
            && implementationType !== ImplementationType.Dropbox
            && implementationType !== ImplementationType.DocsToNeos ? (
            <div className="col-span-12">
              <div className={cn(isUploading && "opacity-50 pointer-events-none")}>
                <Card>
                  <CardContent>
                    <h4 className="text-lg font-semibold">
                      Mappings
                    </h4>
                    <Separator className="mt-2" />
                    <MappingsSettingsSection
                      implementationType={implementationType}
                      disabled={userRole === UserRole.Viewer}
                    />
                  </CardContent>
                </Card>
              </div>
            </div>
          ) : (
            <></>
          )}

          <div className="col-span-12">
            <div className={cn(isUploading && "opacity-50 pointer-events-none")}>
              <Card>
                <CardContent>
                  <h4 className="text-lg font-semibold">
                    Migration
                  </h4>
                  <Separator className="mt-2 mb-4" />
                  <MigrationConfigurationSection
                    implementationType={implementationType}
                    disabled={userRole === UserRole.Viewer}
                  />
                </CardContent>
              </Card>
            </div>
          </div>

        </div>
      </form>
    </FormProvider>
  );
};

export default ClientConfigurationForm;
